// #496 (S3): the per-line "Detalii" field of the products step follows the
// shop setting of return-api (`GET /settings`): `portal_details_mode`
// SHOWN / PHOTO_ONLY / HIDDEN and `portal_details_max_length` 1..500. An API
// without the fields keeps the behaviour before #496: SHOWN, 500 (the column).
// Every length counts Unicode code points, as the API (MySQL VARCHAR) does:
// an emoji counts once.

export type PortalDetailsMode = 'SHOWN' | 'PHOTO_ONLY' | 'HIDDEN';

export const DETAILS_MAX_LENGTH = 500;

const MODES: readonly PortalDetailsMode[] = ['SHOWN', 'PHOTO_ONLY', 'HIDDEN'];

export interface PortalDetailsSettings {
  mode: PortalDetailsMode;
  maxLength: number;
}

export const detailsModeOf = (raw: unknown): PortalDetailsMode =>
  MODES.find(mode => mode === raw) ?? 'SHOWN';

export const detailsMaxLengthOf = (raw: unknown): number =>
  typeof raw === 'number' &&
  Number.isInteger(raw) &&
  raw >= 1 &&
  raw <= DETAILS_MAX_LENGTH
    ? raw
    : DETAILS_MAX_LENGTH;

export const detailsSettingsOf = (
  settings:
    | { portal_details_mode?: unknown; portal_details_max_length?: unknown }
    | null
    | undefined
): PortalDetailsSettings => ({
  mode: detailsModeOf(settings?.portal_details_mode),
  maxLength: detailsMaxLengthOf(settings?.portal_details_max_length),
});

// SHOWN: every line; PHOTO_ONLY: a line the photo rules select; HIDDEN: none
export const isDetailsShown = (
  mode: PortalDetailsMode,
  requirePhoto: boolean
): boolean => mode === 'SHOWN' || (mode === 'PHOTO_ONLY' && requirePhoto);

// the text is required only on a photo line in SHOWN (the server rule)
export const isDetailsRequired = (
  mode: PortalDetailsMode,
  requirePhoto: boolean
): boolean => mode === 'SHOWN' && requirePhoto;

// the text of a line as it may leave the step: '' when the field is hidden
export const visibleDetails = (
  mode: PortalDetailsMode,
  requirePhoto: boolean,
  details: string
): string => (isDetailsShown(mode, requirePhoto) ? details : '');

export const codePointLength = (text: string): number =>
  Array.from(text).length;

// The textarea's next value under `max` code points (the native maxLength
// counts UTF-16 units and would refuse a text of emoji within the limit).
// Within the limit, or not longer than before (a text over a lowered limit
// may shrink): as typed. Otherwise only the inserted part is cut, never an
// emoji in half and never the text around the cursor; a text already at or
// over the limit does not grow.
export const limitCodePoints = (
  previous: string,
  next: string,
  max: number
): string => {
  const nextPoints = Array.from(next);
  if (nextPoints.length <= max) return next;
  const previousPoints = Array.from(previous);
  if (nextPoints.length <= previousPoints.length) return next;
  let prefix = 0;
  while (
    prefix < previousPoints.length &&
    prefix < nextPoints.length &&
    previousPoints[prefix] === nextPoints[prefix]
  )
    prefix++;
  let suffix = 0;
  while (
    suffix < previousPoints.length - prefix &&
    suffix < nextPoints.length - prefix &&
    previousPoints[previousPoints.length - 1 - suffix] ===
      nextPoints[nextPoints.length - 1 - suffix]
  )
    suffix++;
  const room = max - (prefix + suffix);
  if (room <= 0) return previous;
  const inserted = nextPoints.slice(prefix, nextPoints.length - suffix);
  return [
    ...nextPoints.slice(0, prefix),
    ...inserted.slice(0, room),
    ...nextPoints.slice(nextPoints.length - suffix),
  ].join('');
};

export type DetailsErrorCode = 'DESCRIPTION_TOO_LONG' | 'DESCRIPTION_REQUIRED';

// a 400 of POST /returns on the text of a line (return-api #496 S1);
// `line_index` is 0-based in the request's `return_products`
export interface DetailsError {
  code: DetailsErrorCode;
  line_index: number;
  max_length?: number;
}

export const detailsErrorOf = (
  status: number,
  body: unknown
): DetailsError | null => {
  if (status !== 400 || !body || typeof body !== 'object') return null;
  const { code, line_index, max_length } = body as Record<string, unknown>;
  if (code !== 'DESCRIPTION_TOO_LONG' && code !== 'DESCRIPTION_REQUIRED')
    return null;
  if (
    typeof line_index !== 'number' ||
    !Number.isInteger(line_index) ||
    line_index < 0
  )
    return null;
  return {
    code,
    line_index,
    ...(typeof max_length === 'number' && Number.isInteger(max_length)
      ? { max_length }
      : {}),
  };
};
